import React, { useState, useEffect } from 'react';
import { Modal, Button, Descriptions, Input, Form, message } from 'antd';
import { CarOutlined, EditOutlined, SaveOutlined, CloseOutlined } from '@ant-design/icons';
import api from '../../../services/api';

const ShippingInfoModal = ({ visible, onCancel, selectedOrder, onOrderUpdated }) => {
    const [form] = Form.useForm();
    const [isEditMode, setIsEditMode] = useState(false);
    const [loading, setLoading] = useState(false);
    const [localOrderData, setLocalOrderData] = useState(null);

    // Initialize form data when selectedOrder changes
    useEffect(() => {
        if (selectedOrder) {
            setLocalOrderData(selectedOrder);
            const formValues = {
                shipName: selectedOrder.shipName || '',
                shipAddressLine1: selectedOrder.shipAddressLine1 || '',
                shipAddressLine2: selectedOrder.shipAddressLine2 || '',
                shipAddressLine3: selectedOrder.shipAddressLine3 || '',
                shipCity: selectedOrder.shipCity || '',
                shipCounty: selectedOrder.shipCounty || '',
                shipDistrict: selectedOrder.shipDistrict || '',
                shipStateOrRegion: selectedOrder.shipStateOrRegion || '',
                shipPostalCode: selectedOrder.shipPostalCode || '',
                shipCountryCode: selectedOrder.shipCountryCode || '',
                shipPhone: selectedOrder.shipPhone || '',
                shipEmail: selectedOrder.shipEmail || ''
            };
            console.log('Setting form values:', formValues);
            form.setFieldsValue(formValues);
        }
    }, [selectedOrder, form]);

    // Reset form when modal is closed
    useEffect(() => {
        if (!visible) {
            setIsEditMode(false);
            form.resetFields();
        }
    }, [visible, form]);

    // Force form to update when entering edit mode
    useEffect(() => {
        if (isEditMode && localOrderData) {
            // Use setTimeout to ensure the form is properly rendered before setting values
            setTimeout(() => {
                const formValues = {
                    shipName: localOrderData.shipName || '',
                    shipAddressLine1: localOrderData.shipAddressLine1 || '',
                    shipAddressLine2: localOrderData.shipAddressLine2 || '',
                    shipAddressLine3: localOrderData.shipAddressLine3 || '',
                    shipCity: localOrderData.shipCity || '',
                    shipCounty: localOrderData.shipCounty || '',
                    shipDistrict: localOrderData.shipDistrict || '',
                    shipStateOrRegion: localOrderData.shipStateOrRegion || '',
                    shipPostalCode: localOrderData.shipPostalCode || '',
                    shipCountryCode: localOrderData.shipCountryCode || '',
                    shipPhone: localOrderData.shipPhone || '',
                    shipEmail: localOrderData.shipEmail || ''
                };
                console.log('Force updating form values in edit mode:', formValues);
                form.setFieldsValue(formValues);
            }, 100);
        }
    }, [isEditMode, localOrderData, form]);

    const handleEdit = () => {
        // Set form values to current data before entering edit mode
        if (localOrderData) {
            const formValues = {
                shipName: localOrderData.shipName || '',
                shipAddressLine1: localOrderData.shipAddressLine1 || '',
                shipAddressLine2: localOrderData.shipAddressLine2 || '',
                shipAddressLine3: localOrderData.shipAddressLine3 || '',
                shipCity: localOrderData.shipCity || '',
                shipCounty: localOrderData.shipCounty || '',
                shipDistrict: localOrderData.shipDistrict || '',
                shipStateOrRegion: localOrderData.shipStateOrRegion || '',
                shipPostalCode: localOrderData.shipPostalCode || '',
                shipCountryCode: localOrderData.shipCountryCode || '',
                shipPhone: localOrderData.shipPhone || '',
                shipEmail: localOrderData.shipEmail || ''
            };
            console.log('Setting form values for edit mode:', formValues);
            form.setFieldsValue(formValues);
        }
        setIsEditMode(true);
    };

    const handleCancel = () => {
        if (isEditMode) {
            setIsEditMode(false);
            // Reset form to original values from selectedOrder
            if (selectedOrder) {
                form.setFieldsValue({
                    shipName: selectedOrder.shipName || '',
                    shipAddressLine1: selectedOrder.shipAddressLine1 || '',
                    shipAddressLine2: selectedOrder.shipAddressLine2 || '',
                    shipAddressLine3: selectedOrder.shipAddressLine3 || '',
                    shipCity: selectedOrder.shipCity || '',
                    shipCounty: selectedOrder.shipCounty || '',
                    shipDistrict: selectedOrder.shipDistrict || '',
                    shipStateOrRegion: selectedOrder.shipStateOrRegion || '',
                    shipPostalCode: selectedOrder.shipPostalCode || '',
                    shipCountryCode: selectedOrder.shipCountryCode || '',
                    shipPhone: selectedOrder.shipPhone || '',
                    shipEmail: selectedOrder.shipEmail || ''
                });
                // Also reset local data to original values
                setLocalOrderData(selectedOrder);
            }
        } else {
            onCancel();
        }
    };

    const handleModalClose = () => {
        // Always close modal regardless of edit mode
        onCancel();
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            const values = await form.validateFields();
            
            // Find the order ID - try multiple possible paths
            const orderId = selectedOrder?.id || selectedOrder?.order?.id || selectedOrder?.orderId;
            if (!orderId) {
                message.error('Không tìm thấy ID đơn hàng');
                console.error('Selected order data:', selectedOrder);
                return;
            }

            console.log('Updating order shipping info:', { orderId, values });

            // Call API to update shipping info
            const response = await api.patch(`/orders/${orderId}/shipping-info`, values);

            if (response.data?.success) {
                message.success('Cập nhật thông tin giao hàng thành công');
                setIsEditMode(false);
                
                // Update local data with the new values
                const updatedData = { ...localOrderData, ...values };
                setLocalOrderData(updatedData);
                
                // Notify parent component if callback provided
                if (onOrderUpdated) {
                    onOrderUpdated(updatedData);
                }
            } else {
                message.error(response.data?.message || 'Lỗi khi cập nhật thông tin giao hàng');
            }
        } catch (error) {
            console.error('Error updating shipping info:', error);
            const errorMessage = error.response?.data?.message || 'Lỗi khi cập nhật thông tin giao hàng';
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    };

    const renderField = (label, fieldName, placeholder) => {
        if (isEditMode) {
            return (
                <Form.Item 
                    name={fieldName}
                    style={{ margin: 0 }}
                    rules={[
                        {
                            max: 255,
                            message: `${label} không được vượt quá 255 ký tự`
                        }
                    ]}
                    initialValue={localOrderData?.[fieldName] || ''}
                >
                    <Input 
                        placeholder={placeholder}
                        maxLength={255}
                        defaultValue={localOrderData?.[fieldName] || ''}
                    />
                </Form.Item>
            );
        }
        
        // In view mode, show value from localOrderData
        const value = localOrderData?.[fieldName] || '';
        return value || '-';
    };

    return (
        <Modal
            title={
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <CarOutlined style={{ color: '#1890ff' }} />
                    <span>Shipping Information</span>
                    {!isEditMode && (
                        <Button 
                            type="link" 
                            icon={<EditOutlined />}
                            onClick={handleEdit}
                            style={{ marginLeft: 'auto' }}
                        >
                            Edit
                        </Button>
                    )}
                </div>
            }
            open={visible}
            onCancel={handleModalClose}
            footer={
                isEditMode ? [
                    <Button key="cancel" onClick={handleCancel}>
                        <CloseOutlined /> Cancel
                    </Button>,
                    <Button 
                        key="save" 
                        type="primary" 
                        loading={loading}
                        onClick={handleSave}
                    >
                        <SaveOutlined /> Save
                    </Button>
                ] : [
                    <Button key="close" type="primary" onClick={handleModalClose}>
                        Close
                    </Button>
                ]
            }
            width={700}
            styles={{
                body: {
                    padding: '24px' 
                }
            }}
        >
            {localOrderData && (
                <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    <Form 
                        form={form}
                        layout="vertical"
                        preserve={false}
                        key={`shipping-form-${isEditMode ? 'edit' : 'view'}`}
                    >
                        <Descriptions
                            bordered
                            column={1}
                            size="small"
                            styles={{
                                label: {
                                    width: '180px',
                                    backgroundColor: '#fafafa',
                                    fontWeight: 500
                                },
                                content: {
                                    backgroundColor: '#fff',
                                    padding: '12px 16px'
                                }
                            }}
                        >
                            <Descriptions.Item label="Recipient Name">
                                {renderField('Recipient Name', 'shipName', 'Enter recipient name')}
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="Address Line 1">
                                {renderField('Address Line 1', 'shipAddressLine1', 'Enter address line 1')}
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="Address Line 2">
                                {renderField('Address Line 2', 'shipAddressLine2', 'Enter address line 2')}
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="Address Line 3">
                                {renderField('Address Line 3', 'shipAddressLine3', 'Enter address line 3')}
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="City">
                                {renderField('City', 'shipCity', 'Enter city')}
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="County">
                                {renderField('County', 'shipCounty', 'Enter county')}
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="District">
                                {renderField('District', 'shipDistrict', 'Enter district')}
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="State/Region">
                                {renderField('State/Region', 'shipStateOrRegion', 'Enter state or region')}
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="Postal Code">
                                {renderField('Postal Code', 'shipPostalCode', 'Enter postal code')}
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="Country Code">
                                {renderField('Country Code', 'shipCountryCode', 'Enter country code')}
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="Phone Number">
                                {renderField('Phone Number', 'shipPhone', 'Enter phone number')}
                            </Descriptions.Item>
                            
                            <Descriptions.Item label="Email">
                                {renderField('Email', 'shipEmail', 'Enter email address')}
                            </Descriptions.Item>
                        </Descriptions>
                    </Form>
                </div>
            )}
        </Modal>
    );
};

export default ShippingInfoModal;